import * as React from 'react'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormError } from '@/components/ui/form-error'
import { useApiTokenChanges } from '@/hooks/useApiTokens'
import type { ApiTokenDto } from '@lib/dto/api-token.dto'
import type { McpWriteDto } from '@lib/dto/mcp-write.dto'

interface ApiTokenChangesDialogProps {
  /** The token whose changes to show; null while the dialog is closed. */
  token: ApiTokenDto | null
  onClose: () => void
}

interface ChangeRowProps {
  change: McpWriteDto
}

/** A tool's name as a person reads it: `update_plan` is "Update plan". */
function toolName(tool: string): string {
  const words = tool.replace(/_/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function at(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** One change: what it was, then when, with which tool, and the way back to it. */
function ChangeRow({ change }: ChangeRowProps): React.ReactElement {
  return (
    <li className="py-3">
      <p className="text-sm text-ink">{change.summary}</p>
      <p className="mt-1 text-xs text-ink-muted">
        {at(change.createdAt)} · {toolName(change.tool)}
        {change.eventId !== null ? (
          <>
            {' · '}
            <Link to={`/events/${change.eventId}/plan`} className="underline-offset-2 hover:underline">
              Open plan
            </Link>
          </>
        ) : null}
      </p>
    </li>
  )
}

/**
 * What a connection has changed in the app, newest first: the answer to "what
 * has this done?" for the person who approved it, without anyone reading logs.
 * See docs/mcp-server.md, slice 4.
 */
export function ApiTokenChangesDialog({ token, onClose }: ApiTokenChangesDialogProps): React.ReactElement {
  const changes = useApiTokenChanges(token?.id ?? null)

  function renderChanges(): React.ReactElement {
    if (changes.isPending) {
      return (
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
        </p>
      )
    }
    if (changes.isError) {
      return <FormError>These changes could not be loaded. Try again in a moment.</FormError>
    }
    if (changes.data.writes.length === 0) {
      return <p className="text-sm text-ink-muted">Nothing yet.</p>
    }
    return (
      <ol className="divide-y divide-line">
        {changes.data.writes.map((change) => (
          <ChangeRow key={change.id} change={change} />
        ))}
      </ol>
    )
  }

  return (
    <Dialog
      open={token !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>What {token?.name ?? 'this token'} changed</DialogTitle>
          <DialogDescription>Everything changed through this connection, newest first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">{renderChanges()}</div>
      </DialogContent>
    </Dialog>
  )
}
